import Image from 'next/image'
import { Icon } from '@/icons/Icon'
import { User } from '@/payload-types'
import { buildMediaUrl } from '@/collections/Media/utils'

type AppProps = {
  app: string
}

type InstructorProps = {
  instructor: Pick<User, 'id' | 'name' | 'image' | 'authorDescription' | 'headshot'>
}

const experienceFrom = (date: string) => {
  const yearsExperience = Math.floor(
    (Date.now() - new Date(date).getTime()) / 1000 / 60 / 60 / 24 / 365,
  )

  return yearsExperience
}

export default function CourseInstructors({ app, instructor }: AppProps & InstructorProps) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-7 border-b border-gray-200 pb-8 dark:border-white/10">
        <InstructorList instructor={instructor} />
        <InstructorExperience app={app} />
      </div>
      <InstructorCopy />
    </div>
  )
}

export function InstructorList({ instructor }: InstructorProps) {
  const uploadedHeadshot =
    instructor.headshot && typeof instructor.headshot !== 'number' ? instructor.headshot : null
  const uploadedHeadshotUrl =
    uploadedHeadshot?.url ||
    (uploadedHeadshot?.filename ? buildMediaUrl(uploadedHeadshot.filename) : null)
  const authorImage = uploadedHeadshotUrl || instructor.image || null
  const authorImageAlt = uploadedHeadshot?.alt || instructor.name || 'Course Instructor'

  return (
    <section className="flex w-full flex-col gap-4 xs:flex-row">
      <div className="relative flex w-full overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
        <div className="bg-striped w-8.5 grow border-r border-gray-200 dark:border-white/10"></div>
        <div className="flex w-full flex-col gap-1 px-3 py-6 text-center">
          {authorImage && (
            <Image
              src={authorImage}
              alt={authorImageAlt}
              width={48}
              height={48}
              className="mx-auto mb-3 overflow-hidden rounded-full"
            />
          )}
          <p className="font-medium text-gray-950 dark:text-white">{instructor.name}</p>
          <p className="text-14 text-black/60 dark:text-white/60">Co-Founder & CMO</p>
        </div>
        <Icon name="clipboard-user" className="absolute right-3.5 top-4 text-primary-500" />
      </div>
    </section>
  )
}

export function InstructorExperience({ app }: AppProps) {
  const yearsExperience = experienceFrom('2010-01-01')
  const appExperience = experienceFrom(app === 'Motion' ? '2022-01-01' : '2018-01-01')

  const experience = [
    {
      value: `${yearsExperience}+ years`,
      label: 'of experience',
    },
    {
      value: `${appExperience}+ yrs`,
      label: `using ${app}`,
    },
    ...(app === 'Motion' ? [{ value: '7 yrs', label: 'using Asana' }] : []),
  ]

  return (
    <div className="flex w-full flex-col gap-4 sm:flex-row md:flex-col lg:flex-row">
      {experience.map((item, index) => (
        <div
          key={index}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 dark:border-white/10"
        >
          <Icon name="badge-check" className="text-primary-500" />
          <p className="flex gap-1 truncate text-13 text-gray-950 dark:text-white">
            <span className="font-medium">{item.value}</span> {item.label}
          </p>
        </div>
      ))}
    </div>
  )
}

export function InstructorCopy() {
  return (
    <section className="flex flex-col gap-4">
      <p>
        For the past decade, we&apos;ve been on the ground floor with hundreds of businesses doing
        anywhere from $500K-$50M in revenue, helping them find the best path forward with their
        software choices and internal processes.
      </p>
      <p>
        We cut our teeth in software implementation—learning a business&apos; processes, mapping
        them to the right software, building custom integrations to automate manual tasks, and
        training teams on how to adopt new tools while documenting all the steps to the process.
      </p>
      <p>That being said, we understand a few critical things:</p>
      <ol className="flex list-decimal flex-col gap-4 pl-4.5">
        <li>
          <span className="font-medium text-gray-950 dark:text-white">
            Implementing software is a huge investment.
          </span>{' '}
          And we&apos;re not talking about the subscription cost. We&apos;re talking about the
          opportunity cost of time spent researching, setting up, and training team members on each
          new tool.
        </li>
        <li>
          Doing it right from the beginning pays off enormously in the years ahead. We saw time and
          time again, that teams with efficient processes are less stressed and can actually focus
          on the strategic areas of the business.
        </li>
      </ol>
      <p>
        When we started publishing the advice we&apos;d been giving to customers privately, we began
        receiving messages from teams all over, thanking us for helping them gain clarity.
      </p>
      This shift led us to stop taking on individual clients and focus all our efforts on “software
      education at scale.” Since then, we&apos;ve helped nearly 2 million people online with their
      software choices—and we feel like we&apos;re just getting started.
    </section>
  )
}
